import { useQuery } from '@tanstack/react-query'
import { z } from 'zod'

import { api } from '@/api/client'
import { EntityCrud, type FieldDef } from '@/components/entity-crud'

import { CONTACT_FIELDS, contactSchema, emptyContact, toContactValues } from './contact'
import { currenciesKey, fetchCurrencies, fetchSuppliers, suppliersKey, type Supplier } from './api'

const schema = contactSchema.extend({
  shipping_costs: z.string(),
  default_currency: z.string(),
})
type Values = z.infer<typeof schema>

export function SuppliersScreen() {
  const query = useQuery({ queryKey: suppliersKey, queryFn: fetchSuppliers })
  const currencies = useQuery({ queryKey: currenciesKey, queryFn: fetchCurrencies })

  const fields: FieldDef<Values>[] = [
    ...(CONTACT_FIELDS as unknown as FieldDef<Values>[]),
    {
      name: 'default_currency',
      label: 'Default currency',
      type: 'select',
      placeholder: 'None',
      options: [
        { value: '', label: 'None' },
        ...(currencies.data ?? []).map((currency) => ({
          value: currency.iso_code,
          label: `${currency.iso_code} — ${currency.name}`,
        })),
      ],
    },
    {
      name: 'shipping_costs',
      label: 'Shipping',
      type: 'number',
      placeholder: '0.00',
      description: 'Flat shipping cost, in this supplier’s currency.',
    },
  ]

  return (
    <EntityCrud<Supplier, Values>
      entity="suppliers"
      singular="supplier"
      rows={query.data}
      isPending={query.isPending}
      error={query.error}
      queryKey={suppliersKey}
      searchable
      attachmentOwnerType="supplier"
      keyOf={(row) => row.id}
      labelOf={(row) => row.name}
      columns={[
        { header: 'Name', cell: (row) => <span className="font-medium">{row.name}</span> },
        {
          header: 'Currency',
          cell: (row) => row.default_currency ?? <span className="text-muted-foreground">—</span>,
        },
        {
          header: 'Shipping',
          className: 'tabular',
          cell: (row) =>
            row.shipping_costs ? (
              Number(row.shipping_costs).toFixed(2)
            ) : (
              <span className="text-muted-foreground">—</span>
            ),
        },
        {
          header: 'Website',
          cell: (row) =>
            row.website ? (
              <a
                href={row.website}
                target="_blank"
                rel="noreferrer noopener"
                className="text-primary hover:underline"
              >
                {row.website.replace(/^https?:\/\//, '')}
              </a>
            ) : (
              <span className="text-muted-foreground">—</span>
            ),
        },
      ]}
      fields={fields}
      schema={schema}
      emptyValues={{ ...emptyContact, shipping_costs: '', default_currency: '' }}
      toValues={(row) => ({
        ...toContactValues(row),
        shipping_costs: row.shipping_costs ?? '',
        default_currency: row.default_currency ?? '',
      })}
      onCreate={(values) => api.POST('/api/suppliers', { body: toBody(values) })}
      onUpdate={(row, values) =>
        api.PUT('/api/suppliers/{id}', {
          params: { path: { id: row.id } },
          body: toBody(values),
        })
      }
      onDelete={(row) => api.DELETE('/api/suppliers/{id}', { params: { path: { id: row.id } } })}
    />
  )
}

/** Blank optional fields mean "not set", not "empty string". */
function toBody(values: Values) {
  return {
    ...values,
    shipping_costs: values.shipping_costs.trim() || null,
    default_currency: values.default_currency.trim() || null,
  }
}
